import React, { useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealTimeUpdate } from './useRealTimeUpdate';
import { saveAppFiles } from '../../../../../actions/docs';
import Editor from '@monaco-editor/react';
import styles from './MonacoEditor.module.css';
import { setAppFileContent, setAppSelectedLines } from '../../../../../slices/docsSlices';

// Función para activar el JSX Highlighter
const activateMonacoJSXHighlighter = async (monacoEditor, monaco, language) => {
  try {
    // Importar las dependencias necesarias
    const { default: traverse } = await import("@babel/traverse");
    const { parse } = await import("@babel/parser");
    const {
      default: MonacoJSXHighlighter,
      JSXTypes,
      makeBabelParse,
    } = await import("monaco-jsx-highlighter");

    // Configurar el parser para JSX/TSX (true para TSX, false para JSX)
    const isTSX = language === 'tsx';
    const parseJSX = makeBabelParse(parse, isTSX);

    console.log(`🎨 Configurando parser para ${isTSX ? 'TSX' : 'JSX'}`);

    // Instanciar el highlighter
    const monacoJSXHighlighter = new MonacoJSXHighlighter(
      monaco,
      parseJSX,
      traverse,
      monacoEditor
    );

    // Iniciar el resaltado JSX
    let disposeJSXHighlighting = monacoJSXHighlighter.highlightOnDidChangeModelContent();
    
    // Agregar comando de comentario JSX
    let disposeJSXCommenting = monacoJSXHighlighter.addJSXCommentCommand();

    // Personalizar colores para JSX
    JSXTypes.JSXText.options.inlineClassName = "JSXElement.JSXText.tastyPizza";
    JSXTypes.JSXIdentifier.options.inlineClassName = "JSXElement.JSXIdentifier";
    JSXTypes.JSXAttribute.options.inlineClassName = "JSXElement.JSXAttribute";
    JSXTypes.JSXAttributeValue.options.inlineClassName = "JSXElement.JSXAttributeValue";
    JSXTypes.JSXExpression.options.inlineClassName = "JSXElement.JSXExpression";
    JSXTypes.JSXClosingElement.options.inlineClassName = "JSXElement.JSXClosingElement";
    JSXTypes.JSXSelfClosingElement.options.inlineClassName = "JSXElement.JSXSelfClosingElement";
    JSXTypes.JSXFragment.options.inlineClassName = "JSXElement.JSXFragment";
    JSXTypes.JSXOpeningElement.options.inlineClassName = "JSXElement.JSXOpeningElement";
    JSXTypes.JSXClosingFragment.options.inlineClassName = "JSXElement.JSXClosingFragment";
    JSXTypes.JSXOpeningFragment.options.inlineClassName = "JSXElement.JSXOpeningFragment";

    return {
      monacoJSXHighlighter,
      disposeJSXHighlighting,
      disposeJSXCommenting,
      toggleJSXHighlighting: () => {
        if (disposeJSXHighlighting) {
          disposeJSXHighlighting();
          disposeJSXHighlighting = null;
          return false;
        }
        disposeJSXHighlighting = monacoJSXHighlighter.highlightOnDidChangeModelContent();
        return true;
      },
      toggleJSXCommenting: () => {
        if (disposeJSXCommenting) {
          disposeJSXCommenting();
          disposeJSXCommenting = null;
          return false;
        }
        disposeJSXCommenting = monacoJSXHighlighter.addJSXCommentCommand();
        return true;
      }
    };
  } catch (error) {
    console.error('Error activating JSX Highlighter:', error);
    return null;
  }
};

const MonacoEditor = ({ 
  value, 
  onChange, 
  language = 'javascript', 
  placeholder = 'Escribe tu código aquí...',
  disabled = false,
  onKeyDown,
  autoFocus = false,
  height = '400px',
  onDoubleClick,
  autoHeight = false,
  filePath = null, // Nueva prop para identificar el archivo
  onFileUpdate = null, // Callback para actualizar el backend
  currentProject = null,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const jsxHighlighterRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const appFiles = useSelector(state => state.docs.app.files);

  // Usar el hook personalizado para actualizaciones en tiempo real
  const updateFileInRealTime = useRealTimeUpdate(filePath, value, onFileUpdate);

  // Función para guardar el archivo con debounce
  const saveFileWithDebounce = useCallback((content) => {
    // Limpiar el timeout anterior si existe
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Configurar nuevo timeout de 10 segundos
    saveTimeoutRef.current = setTimeout(() => {
      console.log('💾 Guardando archivo después de 10 segundos de inactividad:', filePath);
      console.log('currentProject', currentProject)
      // Crear un objeto con el archivo actual y su contenido
      const filesToSave = { ...appFiles };
      filesToSave[filePath] = content;
      
      // Ejecutar saveAppFiles
      dispatch(saveAppFiles({
        files: filesToSave,
        appId: currentProject
    }));
      
      // Limpiar la referencia del timeout
      saveTimeoutRef.current = null;
    }, 5000); // 10 segundos
  }, [filePath, appFiles, dispatch]);

  const handleEditorDidMount = useCallback(async (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Configurar el lenguaje correcto al montar
    const monacoLanguage = getMonacoLanguage(language);
    const currentModel = editor.getModel();
    if (currentModel && currentModel.getLanguageId() !== monacoLanguage) {
      console.log('🚀 Configurando lenguaje inicial:', monacoLanguage);
      const newModel = monaco.editor.createModel(value || '', monacoLanguage);
      editor.setModel(newModel);
    }
    
    // Activar JSX Highlighter para archivos JSX/TSX
    if (language === 'jsx' || language === 'tsx') {
      console.log('🎨 Activando JSX Highlighter para:', language);
      const jsxHighlighter = await activateMonacoJSXHighlighter(editor, monaco, language);
      if (jsxHighlighter) {
        jsxHighlighterRef.current = jsxHighlighter;
        console.log('✅ JSX Highlighter activado correctamente');
      }
    }
    
    if (autoFocus) {
      editor.focus();
    }

    // Agregar evento de doble clic si se proporciona
    if (onDoubleClick) {
      editor.onMouseDown((e) => {
        if (e.event.detail === 2) { // Doble clic
          onDoubleClick(e);
        }
      });
    }

    // Agregar listener para cambios en la selección
    editor.onDidChangeCursorSelection((e) => {
      const selection = e.selection;
      const startLine = selection.startLineNumber;
      const endLine = selection.endLineNumber;
      
      // Solo actualizar si hay una selección real (más de una línea o selección de texto)
      if (startLine !== endLine || selection.startColumn !== selection.endColumn) {
        dispatch(setAppSelectedLines({
          startLine,
          endLine,
          startColumn: selection.startColumn,
          endColumn: selection.endColumn
        }));
      } else {
        // Si no hay selección, limpiar el estado
        dispatch(setAppSelectedLines(null));
      }
    });

    // Configurar el editor
    editor.updateOptions({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      fontSize: 14,
      fontFamily: "'Monaco', 'Ubuntu Mono', 'Consolas', monospace",
      lineNumbers: 'on',
      roundedSelection: false,
      scrollbar: {
        vertical: autoHeight ? 'hidden' : 'visible',
        horizontal: 'visible',
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8
      },
      theme: 'vs-light',
      wordWrap: 'on',
      automaticLayout: true,
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: false,
      overviewRulerBorder: false,
      hideCursorInOverviewRuler: true,
      overviewRulerLanes: 0
    });

    // Aplicar el tema claro
    monaco.editor.setTheme('vs-light');
  }, [language, value, autoFocus, onDoubleClick, dispatch, autoHeight]);

  const handleEditorChange = (value, event) => {
    const newValue = value || '';
    
    // Llamar al onChange original si existe
    if (onChange) {
      onChange(newValue);
    }
    
    // Usar el hook personalizado para actualización en tiempo real
    if (filePath) {
      updateFileInRealTime(newValue);
    }
    
    // Ejecutar el debounce para guardar después de 10 segundos
    if (filePath) {
      saveFileWithDebounce(newValue);
    }
    
    // Ajustar altura automáticamente si está habilitado
    if (autoHeight && editorRef.current) {
      const lineCount = editorRef.current.getModel().getLineCount();
      const lineHeight = editorRef.current.getOption(monaco.editor.EditorOption.lineHeight);
      const newHeight = Math.max(lineCount * lineHeight + 20, 100); // Mínimo 100px
      editorRef.current.getContainerDomNode().style.height = `${newHeight}px`;
      editorRef.current.layout();
    }
  };

  const handleEditorKeyDown = (event) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  // Efecto para ajustar altura cuando cambia el valor
  useEffect(() => {
    if (autoHeight && editorRef.current) {
      const lineCount = editorRef.current.getModel().getLineCount();
      const lineHeight = editorRef.current.getOption(monaco.editor.EditorOption.lineHeight);
      const newHeight = Math.max(lineCount * lineHeight + 20, 100);
      editorRef.current.getContainerDomNode().style.height = `${newHeight}px`;
      editorRef.current.layout();
    }
  }, [value, autoHeight]);

  // Efecto para mantener el foco y la posición del cursor durante la edición
  useEffect(() => {
    if (editorRef.current && autoFocus) {
      // Pequeño delay para asegurar que el editor esté listo
      setTimeout(() => {
        editorRef.current.focus();
        // Mover el cursor al final del contenido
        const model = editorRef.current.getModel();
        if (model) {
          const lastLine = model.getLineCount();
          const lastColumn = model.getLineMaxColumn(lastLine);
          editorRef.current.setPosition({ lineNumber: lastLine, column: lastColumn });
        }
      }, 100);
    }
  }, [autoFocus]);

  // Efecto para actualizar el editor cuando cambia el filePath, valor o lenguaje
  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      const monacoLanguage = getMonacoLanguage(language);
      const currentModel = editorRef.current.getModel();
      const currentValue = editorRef.current.getValue();
      
      // Verificar si necesitamos cambiar el lenguaje
      if (currentModel && currentModel.getLanguageId() !== monacoLanguage) {
        console.log('🔄 Cambiando lenguaje de', currentModel.getLanguageId(), 'a', monacoLanguage);
        
        // Limpiar JSX Highlighter anterior si existe
        if (jsxHighlighterRef.current) {
          if (jsxHighlighterRef.current.disposeJSXHighlighting) {
            jsxHighlighterRef.current.disposeJSXHighlighting();
          }
          if (jsxHighlighterRef.current.disposeJSXCommenting) {
            jsxHighlighterRef.current.disposeJSXCommenting();
          }
          jsxHighlighterRef.current = null;
        }
        
        // Guardar la posición actual del cursor y selección
        const currentPosition = editorRef.current.getPosition();
        const currentSelection = editorRef.current.getSelection();
        
        // Crear un nuevo modelo con el lenguaje correcto
        const newModel = monacoRef.current.editor.createModel(value || '', monacoLanguage);
        editorRef.current.setModel(newModel);
        
        // Reactivar JSX Highlighter si es necesario
        if ((language === 'jsx' || language === 'tsx') && !jsxHighlighterRef.current) {
          console.log('🎨 Reactivando JSX Highlighter para:', language);
          activateMonacoJSXHighlighter(editorRef.current, monacoRef.current, language)
            .then((jsxHighlighter) => {
              if (jsxHighlighter) {
                jsxHighlighterRef.current = jsxHighlighter;
                console.log('✅ JSX Highlighter reactivado correctamente');
              }
            })
            .catch((error) => {
              console.error('Error reactivando JSX Highlighter:', error);
            });
        }
        
        // Restaurar la posición del cursor si es válida
        if (currentPosition && currentPosition.lineNumber > 0 && currentPosition.column > 0) {
          const model = editorRef.current.getModel();
          if (model && currentPosition.lineNumber <= model.getLineCount()) {
            editorRef.current.setPosition(currentPosition);
            // Restaurar la selección si existe
            if (currentSelection) {
              editorRef.current.setSelection(currentSelection);
            }
          } else {
            // Si la posición no es válida, ir al inicio
            editorRef.current.setPosition({ lineNumber: 1, column: 1 });
          }
        } else {
          // Si no hay posición válida, ir al inicio
          editorRef.current.setPosition({ lineNumber: 1, column: 1 });
        }
      } else if (currentValue !== value) {
        // Solo actualizar el contenido si no cambió el lenguaje pero sí el valor
        // Guardar la posición actual del cursor
        const currentPosition = editorRef.current.getPosition();
        
        editorRef.current.setValue(value || '');
        
        // Restaurar la posición del cursor si es válida
        if (currentPosition && currentPosition.lineNumber > 0 && currentPosition.column > 0) {
          const model = editorRef.current.getModel();
          if (model && currentPosition.lineNumber <= model.getLineCount()) {
            editorRef.current.setPosition(currentPosition);
          } else {
            // Si la posición no es válida, ir al inicio
            editorRef.current.setPosition({ lineNumber: 1, column: 1 });
          }
        } else {
          // Si no hay posición válida, ir al inicio
          editorRef.current.setPosition({ lineNumber: 1, column: 1 });
        }
      }
    }
  }, [filePath, value, language]);

  // Efecto de limpieza cuando el componente se desmonta
  useEffect(() => {
    return () => {
      // Limpiar JSX Highlighter al desmontar
      if (jsxHighlighterRef.current) {
        if (jsxHighlighterRef.current.disposeJSXHighlighting) {
          jsxHighlighterRef.current.disposeJSXHighlighting();
        }
        if (jsxHighlighterRef.current.disposeJSXCommenting) {
          jsxHighlighterRef.current.disposeJSXCommenting();
        }
        jsxHighlighterRef.current = null;
      }
      
      // Limpiar el timeout de guardado al desmontar
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, []);

  // Mapear lenguajes de archivo a lenguajes de Monaco
  const getMonacoLanguage = (fileLanguage) => {
    // console.log('fileLanguage', fileLanguage)
    switch (fileLanguage) {
      case 'jsx':
        return 'javascript';
      case 'tsx':
        return 'typescript';
      case 'js':
        return 'javascript';
      case 'ts':
        return 'typescript';
      case 'css':
        return 'css';
      case 'scss':
        return 'scss';
      case 'html':
        return 'html';
      case 'json':
        return 'json';
      case 'md':
        return 'markdown';
      case 'xml':
        return 'xml';
      case 'sql':
        return 'sql';
      case 'yaml':
        return 'yaml';
      case 'yml':
        return 'yaml';
      case 'py':
        return 'python';
      case 'php':
        return 'php';
      case 'java':
        return 'java';
      case 'c':
        return 'c';
      case 'cpp':
        return 'cpp';
      case 'cs':
        return 'csharp';
      case 'rb':
        return 'ruby';
      case 'go':
        return 'go';
      case 'rs':
        return 'rust';
      case 'swift':
        return 'swift';
      case 'kt':
        return 'kotlin';
      case 'sh':
        return 'shell';
      case 'bash':
        return 'shell';
      case 'zsh':
        return 'shell';
      case 'fish':
        return 'shell';
      case 'ps1':
        return 'powershell';
      case 'bat':
        return 'batch';
      case 'cmd':
        return 'batch';
      case 'ini':
        return 'ini';
      case 'toml':
        return 'toml';
      case 'lock':
        return 'json';
      case 'log':
        return 'plaintext';
      case 'txt':
        return 'plaintext';
      default:
        return 'javascript';
    }
  };

  return (
    <div className={styles.monacoContainer}>
      <Editor
        height={height}
        defaultLanguage={getMonacoLanguage(language)}
        language={getMonacoLanguage(language)}
        value={value}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        onKeyDown={handleEditorKeyDown}
        options={{
          readOnly: disabled,
          placeholder: placeholder,
          wordWrap: 'on',
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 14,
          fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
          lineNumbers: 'on',
          roundedSelection: false,
          automaticLayout: true,
          tabSize: 2,
          insertSpaces: true,
          detectIndentation: false,
          theme: 'vs-light'
        }}
        theme="vs-light"
      />
    </div>
  );
};

export default MonacoEditor; 